/**
 * Shared client-side image sizing for every ngx-image-cropper flow (SUN-820).
 *
 * WHY: full-resolution photos from high-megapixel phone cameras (the Samsung
 * Z Fold's sensor reaches 200MP) decode to hundreds of MB of bitmap. Feeding one
 * straight into the cropper — which also kept a base64 copy via outputType="both"
 * — plus an uncapped square output canvas overran the Android WebView's
 * per-renderer memory budget. The renderer crashed and repainted the page blank
 * white. Downscaling the picked file before it reaches the cropper and capping
 * every output canvas keeps peak memory bounded on any device.
 */

const INPUT_MAX_DIMENSION: number = 1280;
const OUTPUT_MAX_EDGE: number = 512;
const JPEG_QUALITY: number = 0.9;

/** Landscape 4:3 (4 wide × 3 tall) — impact points and fund-a-needs. */
export const LANDSCAPE_4_3: number = 4 / 3;

export interface ToJpgOptions {
  /** File name for the produced JPEG (e.g. 'avatar.jpg'). */
  name: string;
  /** width / height of the output canvas. Default 1 (square). */
  aspectRatio?: number;
  /** Longest edge of the output in px. Default 512. */
  maxEdge?: number;
}

/**
 * Re-encode a picked image File downscaled so its longest edge is at most
 * `maxDimension`, BEFORE it reaches the cropper. The browser's drawImage bakes in
 * EXIF orientation, so portrait phone photos stay upright. Images already within
 * the cap are never upscaled.
 */
export function downscaleImageFile(file: File, maxDimension: number = INPUT_MAX_DIMENSION): Promise<File> {
  return new Promise<File>((resolve: (value: File) => void, reject: (reason: Error) => void) => {
    const objectUrl: string = URL.createObjectURL(file);
    const img: HTMLImageElement = new Image();

    img.onload = (): void => {
      URL.revokeObjectURL(objectUrl);
      const { width, height } = scaleToFit(img.naturalWidth, img.naturalHeight, maxDimension);
      const canvas: HTMLCanvasElement = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas 2D context unavailable'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob: Blob | null): void => {
          if (blob) {
            resolve(new File([blob], file.name || 'image.jpg', { type: 'image/jpeg' }));
          } else {
            reject(new Error('Failed to downscale image.'));
          }
        },
        'image/jpeg',
        JPEG_QUALITY,
      );
    };

    img.onerror = (): void => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load selected image.'));
    };

    img.src = objectUrl;
  });
}

/**
 * Convert a (cropped) image URL into a JPEG File of a fixed aspect ratio,
 * centered on a white background, capped so the longest edge is `maxEdge`.
 * `aspectRatio` defaults to 1 (square) to preserve avatar/product behavior.
 */
export function toJpg(imageUrl: string, options: ToJpgOptions): Promise<File> {
  const aspectRatio: number = options.aspectRatio ?? 1;
  const maxEdge: number = options.maxEdge ?? OUTPUT_MAX_EDGE;

  return new Promise<File>((resolve: (value: File) => void, reject: (reason: Error) => void) => {
    const img: HTMLImageElement = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = (): void => {
      const { width: canvasWidth, height: canvasHeight } = canvasSizeForAspect(aspectRatio, maxEdge);
      const canvas: HTMLCanvasElement = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas 2D context unavailable'));
        return;
      }

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      const { width: drawWidth, height: drawHeight } = containSize(
        img.naturalWidth,
        img.naturalHeight,
        canvasWidth,
        canvasHeight,
      );
      const offsetX: number = (canvasWidth - drawWidth) / 2;
      const offsetY: number = (canvasHeight - drawHeight) / 2;
      ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

      canvas.toBlob(
        (blob: Blob | null): void => {
          if (blob) {
            resolve(new File([blob], options.name, { type: 'image/jpeg' }));
          } else {
            reject(new Error('Failed to convert image to JPG.'));
          }
        },
        'image/jpeg',
        JPEG_QUALITY,
      );
    };

    img.onerror = (): void => reject(new Error('Failed to load image.'));
    img.src = imageUrl;
  });
}

/** Scale (down only) so the longest edge is at most `maxDimension`. */
function scaleToFit(width: number, height: number, maxDimension: number): { width: number; height: number } {
  const longestEdge: number = Math.max(width, height);
  const scale: number = Math.min(maxDimension / longestEdge, 1);
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/** Output canvas dimensions for `aspectRatio` with the longest edge == `maxEdge`. */
function canvasSizeForAspect(aspectRatio: number, maxEdge: number): { width: number; height: number } {
  if (aspectRatio >= 1) {
    return { width: maxEdge, height: Math.round(maxEdge / aspectRatio) };
  }
  return { width: Math.round(maxEdge * aspectRatio), height: maxEdge };
}

/** Largest size preserving aspect ratio that fits inside the box (contain). */
function containSize(
  srcWidth: number,
  srcHeight: number,
  boxWidth: number,
  boxHeight: number,
): { width: number; height: number } {
  const scale: number = Math.min(boxWidth / srcWidth, boxHeight / srcHeight);
  return { width: Math.round(srcWidth * scale), height: Math.round(srcHeight * scale) };
}
